import React, { useState } from 'react';
import { AuthPermissions, PaylinkResponse } from 'skipify-types';

import DetailModal from '../PaylinkDetails/modal';
import {
  useCancelPaylinkMutation,
  useSendPaylinkMutation,
} from '../../../../store/modules/paylink/api';
import { toast } from '../../../../util/toast';
import { ActionDialogModal } from '../ActionDialog/modal';
import { PaylinkActions, getAllowedActions } from '../../constants';
import { PaylinkDetailsPage } from '../PaylinkDetails';
import { TransactionInfoDialog } from '../TransactionInfoDialog';
import { useGetPublicMerchantQuery } from '../../../../store/modules/merchant/api';
import { useMerchant } from '../../../../contexts/MerchantContext';
import { ActionsPopover } from '../../../../common/ActionsPopover';

interface Props {
  paylink: PaylinkResponse;
}

export const PaylinkActionsPopover = ({ paylink }: Props) => {
  const { status, sendEmail, sendSms, paylinkId } = paylink;
  const { merchantId, merchantName: name, canAccess } = useMerchant();
  const { data: publicData } = useGetPublicMerchantQuery(merchantId, { skip: !merchantId });
  const merchantName = publicData?.branding?.displayName ?? name;
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isResendDialogOpen, setIsResendDialogOpen] = useState(false);
  const [detailsOpenPage, setDetailsOpenPage] = useState<PaylinkDetailsPage>('details');
  const [isTransactionInfoDialogOpen, setIsTransactionInfoDialogOpen] = useState(false);

  const [sendPaylink, { isLoading: isSending }] = useSendPaylinkMutation();
  const [cancelPaylink, { isLoading: isCanceling }] = useCancelPaylinkMutation();

  const handleCancel = () => {
    cancelPaylink({ paylinkId })
      .unwrap()
      .then(() => {
        setIsCancelDialogOpen(false);
        toast.success('Paylink successfully cancelled.');
      })
      .catch(() => {
        toast.error('Something went wrong, please try again later.');
      });
  };

  const handleResend = () => {
    sendPaylink({ paylinkId, sendEmail, sendSms })
      .unwrap()
      .then(() => {
        setIsResendDialogOpen(false);
        toast.success('Paylink Resent Successfully');
      })
      .catch(() => {
        toast.error('There was an error Resending this Paylink. Please try again.');
      });
  };

  const allowedActions = getAllowedActions(status);

  return (
    <>
      <ActionsPopover
        ariaLabel="Paylink Actions"
        actions={[
          {
            label: 'Details',
            onClick: () => {
              setIsDetailModalOpen(true);
            },
            disabled:
              !canAccess(AuthPermissions.READ_PAYLINKS) &&
              !canAccess(AuthPermissions.READ_OWN_PAYLINKS),
          },
          {
            label: 'Edit',
            onClick: () => {
              setDetailsOpenPage('edit');
              setIsDetailModalOpen(true);
            },
            disabled:
              !canAccess(AuthPermissions.UPDATE_PAYLINKS) &&
              !canAccess(AuthPermissions.UPDATE_OWN_PAYLINKS),
            shouldRender: allowedActions.includes(PaylinkActions.EDIT),
          },
          {
            label: 'Resend',
            onClick: () => {
              handleResend();
            },
            disabled:
              !canAccess(AuthPermissions.CREATE_PAYLINKS) &&
              !canAccess(AuthPermissions.CREATE_OWN_PAYLINKS),
            shouldRender: allowedActions.includes(PaylinkActions.RESEND),
          },
          {
            label: 'Cancel',
            onClick: () => {
              setIsCancelDialogOpen(true);
            },
            disabled:
              !canAccess(AuthPermissions.CANCEL_PAYLINKS) &&
              !canAccess(AuthPermissions.CANCEL_OWN_PAYLINKS),
            shouldRender: allowedActions.includes(PaylinkActions.CANCEL),
          },
          {
            label: 'Transaction Info',
            onClick: () => {
              setIsTransactionInfoDialogOpen(true);
            },
            disabled:
              !canAccess(AuthPermissions.READ_PAYLINK_TRANSACTIONS) &&
              !canAccess(AuthPermissions.READ_OWN_PAYLINK_TRANSACTIONS),
            shouldRender: allowedActions.includes(PaylinkActions.VIEW_TRANSACTIONS),
          },
          {
            label: 'Refund',
            onClick: () => {
              setDetailsOpenPage('refund');
              setIsDetailModalOpen(true);
            },
            disabled:
              !canAccess(AuthPermissions.REFUND_PAYLINKS) &&
              !canAccess(AuthPermissions.REFUND_OWN_PAYLINKS),
            shouldRender: allowedActions.includes(PaylinkActions.REFUND),
          },
        ]}
      />
      <ActionDialogModal
        isOpen={isResendDialogOpen}
        onProceed={handleResend}
        isSubmitting={isSending}
        onClose={() => setIsResendDialogOpen(false)}
        content={{
          heading: 'Are you sure you want to resend this Paylink?',
          helpingText:
            'Your customer will receive this same Paylink that has already been sent to them.',
          cancelButtonText: 'Cancel',
          proceedButtonText: 'Resend Paylink',
        }}
      />
      <ActionDialogModal
        isOpen={isCancelDialogOpen}
        onProceed={handleCancel}
        isSubmitting={isCanceling}
        onClose={() => setIsCancelDialogOpen(false)}
        content={{
          heading: 'Are you sure you want to cancel this Paylink?',
          helpingText:
            'Your customer will no longer be able to complete this purchase if the Paylink request is cancelled.',
          cancelButtonText: 'Back',
          proceedButtonText: 'Cancel this Paylink',
        }}
      />
      <DetailModal
        merchantId={merchantId}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setDetailsOpenPage('details');
        }}
        merchantName={merchantName}
        paylink={paylink}
        defaultOpenPage={detailsOpenPage}
      />
      {allowedActions.includes(PaylinkActions.VIEW_TRANSACTIONS) && (
        <TransactionInfoDialog
          isOpen={isTransactionInfoDialogOpen}
          onClose={() => setIsTransactionInfoDialogOpen(false)}
          paylink={paylink}
          merchantName={merchantName}
        />
      )}
    </>
  );
};
